// src/components/ClientsTable.jsx
import React, { Suspense, useState, useEffect, useRef } from 'react';
import ClientRow from './ClientRow';
import '../styles/ClientsTable.css';
import '../styles/NotificationStyles.css';
import StartProjectModal from './StartProjectModal';
import ConversationsPanel from './ConversationsPanel';
import { auth, db } from '../firebase';
import { doc, getDocFromServer, serverTimestamp } from 'firebase/firestore';
import { useNotificationsContext } from '../contexts/NotificationsContext';
import { mailboxConnectedForLaunch } from '../utils/mailboxConnection';
import { prepareActionAuditOutboxBatch } from '../utils/actionAudit';
import { buildExactCampaignLaunchOutbox } from '../utils/campaignLaunchOutbox';
import { deriveNonRequestableFields } from '../utils/nonRequestableFields';
import { deriveAskFields, deriveCampaignProperties } from '../utils/campaignAskFields';
import { buildCampaignDraftForbiddenValues } from '../utils/campaignDraftScope';
import { isResultsFeatureAdminUser } from '../utils/adminAccess';
import { isResultsEnabled } from '../utils/resultsFeatureFlags';
import { validateCampaignLaunchDraft } from '../utils/draftGuards';
import { useCampaignAccess } from '../contexts/CampaignAccessContext';
import { useSessionStorage } from '../contexts/SessionStorageContext';
import CampaignMaintenanceDialog from './CampaignMaintenanceDialog';
import ConfirmDialog from './ConfirmDialog';
import { stopCampaign, completeCampaign, reopenCampaign } from '../utils/campaignOperationsClient';
import { verifyCampaignCreationAccess } from '../utils/campaignLaunchGuard';
import { buildCampaignPropertySubject } from '../utils/campaignSubject';
import {
  loadClientResultsCell,
  loadResultsWorkspace,
} from '../generated/resultsWorkspaceLoader';

const CompletedResultsWorkspace = loadResultsWorkspace
  ? React.lazy(loadResultsWorkspace)
  : null;
const ClientResultsCell = loadClientResultsCell
  ? React.lazy(loadClientResultsCell)
  : null;

// Google Sheets icon SVG component
const GoogleSheetsIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#0F9D58"/>
    <path d="M14 2V8H20L14 2Z" fill="#87CEAC"/>
    <path d="M8 13H16V14H8V13Z" fill="white"/>
    <path d="M8 15H16V16H8V15Z" fill="white"/>
    <path d="M8 17H13V18H8V17Z" fill="white"/>
    <path d="M8 11H16V12H8V11Z" fill="white"/>
  </svg>
);

const CAMPAIGN_CONTRACT_ERROR_MESSAGE =
  'Campaign request settings could not be verified. Refresh and try again before starting.';
const CAMPAIGN_START_STATE_ERROR_MESSAGE =
  'This campaign is no longer ready to start. Refresh to see its current status.';

function campaignLaunchGuardMessage(error) {
  return error?.message === CAMPAIGN_START_STATE_ERROR_MESSAGE
    ? CAMPAIGN_START_STATE_ERROR_MESSAGE
    : CAMPAIGN_CONTRACT_ERROR_MESSAGE;
}

function hydrateCampaignLaunchClientData(client, freshClientData) {
  if (!client?.id || !freshClientData || typeof freshClientData !== 'object') {
    throw new Error(CAMPAIGN_CONTRACT_ERROR_MESSAGE);
  }
  const freshStatus = String(freshClientData.status || '').trim().toLowerCase();
  if (freshStatus !== 'new' || freshClientData.pendingDelete === true) {
    throw new Error(CAMPAIGN_START_STATE_ERROR_MESSAGE);
  }
  const hasColumnConfig = freshClientData.columnConfig !== null
    && typeof freshClientData.columnConfig === 'object'
    && !Array.isArray(freshClientData.columnConfig);
  const hasAskFields = Array.isArray(freshClientData.askFields);
  if (!hasColumnConfig && !hasAskFields) throw new Error(CAMPAIGN_CONTRACT_ERROR_MESSAGE);

  const hydratedClient = {
    ...client,
    ...freshClientData,
    columnConfig: hasColumnConfig ? freshClientData.columnConfig : undefined,
    askFields: hasColumnConfig ? undefined : freshClientData.askFields,
    id: client.id,
  };
  [
    'assignedEmails',
    'criteria',
    'emailScripts',
    'emailToContactMap',
    'emailToProperties',
    'secondaryCriteria',
  ].forEach(field => {
    if (!Object.prototype.hasOwnProperty.call(freshClientData, field)) delete hydratedClient[field];
  });
  return hydratedClient;
}

async function hydrateCampaignLaunchClient(client) {
  const uid = auth.currentUser?.uid;
  if (!uid || !client?.id) throw new Error(CAMPAIGN_CONTRACT_ERROR_MESSAGE);

  const snapshot = await getDocFromServer(doc(db, 'users', uid, 'clients', client.id));
  if (!snapshot.exists()) throw new Error(CAMPAIGN_CONTRACT_ERROR_MESSAGE);
  return hydrateCampaignLaunchClientData(client, snapshot.data() || {});
}

function normalizeCampaignEmailList(values) {
  if (!Array.isArray(values)) return [];
  return [...new Set(values
    .map(value => String(value || '').trim().toLowerCase())
    .filter(Boolean))]
    .sort();
}

function sameStringList(left, right) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function isNonEmptySubset(left, right) {
  return left.length > 0 && left.every(value => right.includes(value));
}

function isMultisetSubset(subset, superset) {
  if (subset.length === 0) return false;
  const remaining = new Map();
  superset.forEach(value => remaining.set(value, (remaining.get(value) || 0) + 1));
  return subset.every(value => {
    const available = remaining.get(value) || 0;
    if (available === 0) return false;
    remaining.set(value, available - 1);
    return true;
  });
}

function sameMultiset(left, right) {
  return left.length === right.length && isMultisetSubset(left, right);
}

function campaignPropertyContractKey(email, property) {
  return JSON.stringify([
    String(email || '').trim().toLowerCase(),
    String(property?.rowIndex ?? '').trim(),
    String(property?.address || '').trim(),
    String(property?.city || '').trim(),
    String(property?.propertyName || '').trim(),
  ]);
}

function campaignPropertyContactKey(email, property, contactName = '') {
  return JSON.stringify([
    campaignPropertyContractKey(email, property),
    String(contactName || '').trim(),
  ]);
}

function contactForCampaignEmail(emailToContactMap, email) {
  if (emailToContactMap && Object.prototype.hasOwnProperty.call(emailToContactMap, email)) {
    return emailToContactMap[email] || '';
  }
  const normalizedEmail = String(email || '').trim().toLowerCase();
  return Object.entries(emailToContactMap || {}).find(
    ([candidate]) => String(candidate || '').trim().toLowerCase() === normalizedEmail
  )?.[1] || '';
}

function propertyContractFromMap(emailToProperties, emailToContactMap, assignedEmails) {
  if (!emailToProperties || typeof emailToProperties !== 'object' || Array.isArray(emailToProperties)) {
    return null;
  }

  const entriesByEmail = new Map();
  for (const [rawEmail, properties] of Object.entries(emailToProperties)) {
    const email = String(rawEmail || '').trim().toLowerCase();
    if (!email) return null;
    if (!entriesByEmail.has(email)) entriesByEmail.set(email, []);
    entriesByEmail.get(email).push({
      mappedContact: contactForCampaignEmail(emailToContactMap, rawEmail),
      properties,
    });
  }
  if (!sameStringList([...entriesByEmail.keys()].filter(Boolean).sort(), assignedEmails)) return null;

  const structural = [];
  const personalized = [];
  for (const email of assignedEmails) {
    const groups = entriesByEmail.get(email);
    if (!Array.isArray(groups) || groups.length === 0) return null;
    for (const { mappedContact, properties } of groups) {
      if (!Array.isArray(properties) || properties.length === 0) return null;
      for (const property of properties) {
        if (!property || typeof property !== 'object' || Array.isArray(property)) return null;
        structural.push(campaignPropertyContractKey(email, property));
        personalized.push(campaignPropertyContactKey(
          email,
          property,
          property?.contact || mappedContact
        ));
      }
    }
  }
  return {
    personalized: personalized.sort(),
    structural: structural.sort(),
  };
}

function freshPropertyContract(launchClient, assignedEmailSequence, assignedEmails) {
  const freshMap = launchClient?.emailToProperties;
  const isEmptyMap = freshMap
    && typeof freshMap === 'object'
    && !Array.isArray(freshMap)
    && Object.keys(freshMap).length === 0;
  if (freshMap != null && !isEmptyMap) {
    return propertyContractFromMap(
      freshMap,
      launchClient?.emailToContactMap,
      assignedEmails
    );
  }

  const legacyMap = {};
  assignedEmailSequence.forEach((rawEmail, index) => {
    if (!legacyMap[rawEmail]) legacyMap[rawEmail] = [];
    legacyMap[rawEmail].push({
      rowIndex: index + 3,
      address: `Property ${legacyMap[rawEmail].length + 1}`,
      city: '',
      contact: contactForCampaignEmail(launchClient?.emailToContactMap, rawEmail),
    });
  });
  return propertyContractFromMap(
    legacyMap,
    launchClient?.emailToContactMap,
    assignedEmails
  );
}

function campaignScriptContract(client) {
  return JSON.stringify(['emailScripts', 'criteria', 'secondaryCriteria'].map(field => [
    field,
    Object.prototype.hasOwnProperty.call(client || {}, field),
    client?.[field],
  ]));
}

function stableContractValue(value) {
  if (Array.isArray(value)) return value.map(stableContractValue);
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((result, key) => {
      result[key] = stableContractValue(value[key]);
      return result;
    }, {});
  }
  return value;
}

function campaignLaunchSourceContract(client) {
  const fields = [
    'name',
    'status',
    'pendingDelete',
    'assignedEmails',
    'emailToContactMap',
    'emailToProperties',
    'emailScripts',
    'criteria',
    'secondaryCriteria',
    'columnConfig',
    'askFields',
  ];
  return JSON.stringify(fields.map(field => [
    field,
    Object.prototype.hasOwnProperty.call(client || {}, field),
    stableContractValue(client?.[field]),
  ]));
}

function launchPayloadMatchesFreshCampaign(launchClient, payload) {
  if (!payload?.clientId || String(payload.clientId) !== String(launchClient?.id || '')) return false;
  const freshEmailSequence = [];
  (Array.isArray(launchClient?.assignedEmails) ? launchClient.assignedEmails : []).forEach(value => {
    const email = String(value || '').trim();
    if (email) freshEmailSequence.push(email);
  });
  const freshEmails = normalizeCampaignEmailList(launchClient?.assignedEmails);
  const payloadEmails = normalizeCampaignEmailList(payload?.assignedEmails);
  if (freshEmails.length === 0 || !isNonEmptySubset(payloadEmails, freshEmails)) return false;

  const freshProperties = freshPropertyContract(launchClient, freshEmailSequence, freshEmails);
  const payloadProperties = propertyContractFromMap(
    payload?.emailToProperties,
    null,
    payloadEmails
  );
  if (!freshProperties || !payloadProperties
    || !isMultisetSubset(payloadProperties.structural, freshProperties.structural)) {
    return false;
  }

  if (Array.isArray(payload?.personalizedEmails) && payload.personalizedEmails.length > 0) {
    const personalizedStructural = [];
    const personalizedContacts = [];
    for (const emailData of payload.personalizedEmails) {
      if (!emailData?.property || typeof emailData.property !== 'object'
        || Array.isArray(emailData.property)) return false;
      personalizedStructural.push(campaignPropertyContractKey(emailData?.email, emailData.property));
      personalizedContacts.push(campaignPropertyContactKey(
        emailData?.email,
        emailData.property,
        emailData?.contactName
      ));
    }
    if (!sameMultiset(personalizedStructural, payloadProperties.structural)
      || !isMultisetSubset(personalizedContacts, freshProperties.personalized)) return false;
  }

  return true;
}

function campaignPropertiesForEmail(emailToProperties, email) {
  if (emailToProperties && Object.prototype.hasOwnProperty.call(emailToProperties, email)) {
    return emailToProperties[email] || [];
  }
  return Object.entries(emailToProperties || {}).find(
    ([candidate]) => String(candidate || '').trim().toLowerCase() === String(email || '').trim().toLowerCase()
  )?.[1] || [];
}

function currentCampaignLaunchIsValid({
  baseClient,
  currentData,
  expectedSourceContract,
  payload,
}) {
  try {
    const expectedName = String(baseClient?.name || '').trim();
    const currentName = String(currentData?.name || '').trim();
    if (!expectedName || currentName !== expectedName) return false;
    const currentClient = hydrateCampaignLaunchClientData(baseClient, currentData);
    if (campaignLaunchSourceContract(currentClient) !== expectedSourceContract) return false;
    if (!launchPayloadMatchesFreshCampaign(currentClient, payload)) return false;

    const askFields = Array.isArray(currentClient.askFields)
      ? currentClient.askFields
      : deriveAskFields(currentClient.columnConfig);
    const personalizedEmails = Array.isArray(payload?.personalizedEmails)
      ? payload.personalizedEmails
      : [];
    if (personalizedEmails.length === 0) {
      return validateCampaignLaunchDraft(payload?.script, { askFields }).ok;
    }

    const propertySource = currentClient.emailToProperties
      && typeof currentClient.emailToProperties === 'object'
      && !Array.isArray(currentClient.emailToProperties)
      && Object.keys(currentClient.emailToProperties).length > 0
      ? currentClient.emailToProperties
      : payload.emailToProperties;
    const campaignProperties = deriveCampaignProperties({ emailToProperties: propertySource });
    return personalizedEmails.every(emailData => validateCampaignLaunchDraft(emailData.script, {
      askFields,
      forbiddenValues: buildCampaignDraftForbiddenValues({
        campaignLabel: currentClient.name,
        campaignProperties,
        currentProperty: emailData.property,
        sendMode: emailData.sendMode || 'separate',
        brokerProperties: campaignPropertiesForEmail(propertySource, emailData.email),
      }),
    }).ok);
  } catch (_error) {
    return false;
  }
}

export default function ClientsTable({
  clients,
  autoStartClientId,
  onAutoStartHandled,
  externalActionTarget,
  onExternalActionComplete,
  loading = false,
  clientCampaignCompletion = {}
}) {
  // Get actionable notifications from centralized context (no duplicate listeners!)
  const {
    actionableNotifications,
    allActionableNotifications,
    clientBlockedOutbox = {},
    mailboxConnection
  } = useNotificationsContext();
  // Only a read that finished is allowed to stop a launch; the rule itself lives
  // in one place so the new-campaign wizard holds Start on exactly this answer.
  const mailboxConnected = mailboxConnectedForLaunch(mailboxConnection);
  const campaignAccess = useCampaignAccess();
  const { refresh: refreshCampaignAccess, maintenanceMessage } = campaignAccess;
  const { updateClientInCache } = useSessionStorage();

  const [startingClient, setStartingClient] = useState(null);
  const startProjectRequestRef = useRef(0);
  const [maintenanceDialogMessage, setMaintenanceDialogMessage] = useState(null);
  const [stopConfirmationClient, setStopConfirmationClient] = useState(null);
  const [stoppingClientId, setStoppingClientId] = useState(null);
  const [stopErrors, setStopErrors] = useState({});
  // Marking a campaign complete. `mode` is 'complete' or 'reopen' -- one piece
  // of state for both directions, because they are one decision and its undo.
  const [completionConfirm, setCompletionConfirm] = useState(null);
  const [changingCompletionId, setChangingCompletionId] = useState(null);
  const [completionErrors, setCompletionErrors] = useState({});
  const [expandedConversations, setExpandedConversations] = useState(new Set());
  const [resultsClient, setResultsClient] = useState(null);
  // Track which client should auto-scroll to an action notification
  const [autoScrollToAction, setAutoScrollToAction] = useState(null); // { clientId, notification }
  // Refs for conversation rows to enable scroll-into-view
  const conversationRowRefs = useRef({});
  const resultsEnabled = Boolean(CompletedResultsWorkspace && ClientResultsCell)
    && isResultsEnabled();
  const canUseResultsFeatures = resultsEnabled && isResultsFeatureAdminUser(campaignAccess);
  const showResultsColumn = canUseResultsFeatures;

  // Auto-open StartProjectModal when redirected from AddClientModal
  useEffect(() => {
    let cancelled = false;
    let settled = false;
    let requestId = null;
    if (autoStartClientId && clients.length > 0) {
      const clientToStart = clients.find(c => c.id === autoStartClientId);
      if (clientToStart) {
        requestId = startProjectRequestRef.current + 1;
        startProjectRequestRef.current = requestId;
        setStartingClient(null);
        (async () => {
          try {
            const access = await verifyCampaignCreationAccess(refreshCampaignAccess);
            if (cancelled || requestId !== startProjectRequestRef.current) return;

            if (!access.allowed) {
              setMaintenanceDialogMessage(access.message || maintenanceMessage);
            } else {
              try {
                const hydratedClient = await hydrateCampaignLaunchClient(clientToStart);
                if (!cancelled && requestId === startProjectRequestRef.current) {
                  setStartingClient(hydratedClient);
                }
              } catch (error) {
                if (!cancelled && requestId === startProjectRequestRef.current) {
                  setMaintenanceDialogMessage(campaignLaunchGuardMessage(error));
                }
              }
            }
          } finally {
            settled = true;
            if (!cancelled && onAutoStartHandled) onAutoStartHandled();
          }
        })();
      }
    }
    return () => {
      cancelled = true;
      if (!settled && requestId !== null && requestId === startProjectRequestRef.current) {
        startProjectRequestRef.current += 1;
      }
    };
  }, [autoStartClientId, clients, maintenanceMessage, onAutoStartHandled, refreshCampaignAccess]);

  useEffect(() => {
    const clientId = externalActionTarget?.clientId;
    const notification = externalActionTarget?.notification;
    if (!clientId || !notification || !clients.some(client => client.id === clientId)) return;

    setExpandedConversations(prev => new Set(prev).add(clientId));
    setAutoScrollToAction({ clientId, notification });
  }, [clients, externalActionTarget]);

  // Notifications are now managed by NotificationsContext (no duplicate listeners!)

  const handleStartProject = async (client) => {
    const requestId = startProjectRequestRef.current + 1;
    startProjectRequestRef.current = requestId;
    setStartingClient(null);
    const access = await verifyCampaignCreationAccess(refreshCampaignAccess);
    if (requestId !== startProjectRequestRef.current) return;
    if (!access.allowed) {
      setMaintenanceDialogMessage(access.message || maintenanceMessage);
      return;
    }
    try {
      const hydratedClient = await hydrateCampaignLaunchClient(client);
      if (requestId !== startProjectRequestRef.current) return;
      setStartingClient(hydratedClient);
    } catch (error) {
      if (requestId !== startProjectRequestRef.current) return;
      setMaintenanceDialogMessage(campaignLaunchGuardMessage(error));
    }
  };
  const closeStartModal = () => {
    startProjectRequestRef.current += 1;
    setStartingClient(null);
  };

  const requestCampaignStop = (client) => {
    setStopErrors(current => ({ ...current, [client.id]: '' }));
    setStopConfirmationClient(client);
  };

  const confirmCampaignStop = async () => {
    const client = stopConfirmationClient;
    if (!client || stoppingClientId) return;

    setStoppingClientId(client.id);
    setStopErrors(current => ({ ...current, [client.id]: '' }));
    try {
      const result = await stopCampaign({ clientId: client.id });
      if (!['stopping', 'stopped', 'stop_failed'].includes(result?.status)) {
        throw new Error('Campaign stop did not return a valid status. Refresh before trying again.');
      }
      const cacheUpdate = { status: result.status };
      if (result.stoppedAt) cacheUpdate.stoppedAt = result.stoppedAt;
      if (result.stopLeaseExpiresAt) cacheUpdate.stopLeaseExpiresAt = result.stopLeaseExpiresAt;
      updateClientInCache(client.id, cacheUpdate);
      setStopConfirmationClient(null);
    } catch (stopError) {
      setStopErrors(current => ({
        ...current,
        [client.id]: stopError?.message || 'Campaign could not be stopped. Please try again.'
      }));
      setStopConfirmationClient(null);
    } finally {
      setStoppingClientId(null);
    }
  };

  // MARKING A CAMPAIGN COMPLETE, and un-marking it.
  //
  // This is the decision that BOUNDS THE WORK. Until she takes it, SiteSift has
  // to keep watching every conversation on the campaign for broker replies,
  // forever -- which is why the confirm text says exactly that rather than
  // something reassuring. Nothing is deleted and the decision is reversible.
  const requestMarkComplete = (client) => {
    setCompletionErrors(current => ({ ...current, [client.id]: '' }));
    setCompletionConfirm({ client, mode: 'complete' });
  };

  const requestReopen = (client) => {
    setCompletionErrors(current => ({ ...current, [client.id]: '' }));
    setCompletionConfirm({ client, mode: 'reopen' });
  };

  const confirmCompletionChange = async () => {
    const pending = completionConfirm;
    if (!pending || changingCompletionId) return;
    const { client, mode } = pending;

    setChangingCompletionId(client.id);
    setCompletionErrors(current => ({ ...current, [client.id]: '' }));
    try {
      const call = mode === 'complete' ? completeCampaign : reopenCampaign;
      const result = await call({ clientId: client.id });
      // The cache is only told AFTER the server has written the marker. A row
      // that reads "Completed" while the product is still polling the campaign
      // is the split this whole change exists to close.
      if (!result?.completion?.state) {
        throw new Error('The campaign did not come back with a completion state. Refresh before trying again.');
      }
      updateClientInCache(client.id, { completion: result.completion });
      setCompletionConfirm(null);
    } catch (error) {
      setCompletionErrors(current => ({
        ...current,
        [client.id]: error?.message || (mode === 'complete'
          ? 'Campaign could not be marked complete. Please try again.'
          : 'Campaign could not be reopened. Please try again.')
      }));
      setCompletionConfirm(null);
    } finally {
      setChangingCompletionId(null);
    }
  };

  // Toggle expanded conversations row
  const handleViewConversations = (client) => {
    setExpandedConversations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(client.id)) {
        newSet.delete(client.id);
      } else {
        newSet.add(client.id);
      }
      return newSet;
    });
  };

  const handleOpenResults = async (clientId) => {
    if (!canUseResultsFeatures) {
      alert('Results are temporarily unavailable while this feature is being hardened.');
      return;
    }
    const client = clients.find((c) => c.id === clientId);
    if (client) {
      setResultsClient(client);
      return;
    }
    alert('No campaign data available for this client.');
  };

  const handleOpenSheet = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    const sheetUrl = client?.excelUrl || client?.sheetUrl;
    if (sheetUrl) {
      window.open(sheetUrl, '_blank');
      return;
    }
    alert('No source sheet is available for this client yet.');
  };

  // Handle action button click - expand conversations and scroll to the action
  const handleActionClick = (client, notification = null) => {
    // Use passed notification, or fall back to top actionable notification
    const targetNotification = notification || actionableNotifications[client.id];

    // Set the notification to auto-scroll to
    setAutoScrollToAction({ clientId: client.id, notification: targetNotification });

    // Expand the conversation row if not already expanded
    if (!expandedConversations.has(client.id)) {
      setExpandedConversations(prev => {
        const newSet = new Set(prev);
        newSet.add(client.id);
        return newSet;
      });
    }
  };

  // Show loading skeleton
  if (loading) {
    return (
      <table className="clients-table clients-table--loading">
        <colgroup>
          <col />
          <col />
          <col />
          {showResultsColumn && <col className="clients-table__results-col" />}
          <col className="clients-table__sheet-col" />
        </colgroup>
        <thead>
          <tr>
            <th className="th--status">Status</th>
            <th>Name</th>
            <th>Last Updated</th>
            {showResultsColumn && <th className="clients-table__results-header">Results</th>}
            <th className="clients-table__icon-header" aria-label="Spreadsheet">
              <GoogleSheetsIcon size={20} />
            </th>
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3].map((i) => (
            <tr key={i} className="skeleton-row">
              <td><div className="skeleton skeleton--badge"></div></td>
              <td><div className="skeleton skeleton--text skeleton--name"></div></td>
              <td><div className="skeleton skeleton--text skeleton--date"></div></td>
              {showResultsColumn && <td><div className="skeleton skeleton--icon"></div></td>}
              <td><div className="skeleton skeleton--icon"></div></td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (!clients || clients.length === 0) {
    return (
      <div className="no-clients">
        No clients found. Remove filters or navigate to your Manage Clients page to create a new client.
      </div>
    );
  }

  return (
    <>
      <table className="clients-table">
        <colgroup>
          <col />
          <col />
          <col />
          {showResultsColumn && <col className="clients-table__results-col" />}
          <col className="clients-table__sheet-col" />
        </colgroup>
        <thead>
          <tr>
            <th className="th--status">Status</th>
            <th>Name</th>
            <th>Last Updated</th>
            {showResultsColumn && <th className="clients-table__results-header">Results</th>}
            <th className="clients-table__icon-header" aria-label="Spreadsheet">
              <GoogleSheetsIcon size={20} />
            </th>
          </tr>
        </thead>

        <tbody>
          {clients.map((client) => {
            const notification = actionableNotifications[client.id];
            const hasActionPending = !!notification;
            const isConversationsExpanded = expandedConversations.has(client.id);
            return (
              <React.Fragment key={client.id}>
                <ClientRow
                  client={client}
                  onOpenSheet={handleOpenSheet}
                  onStart={handleStartProject}
                  onActionClick={handleActionClick}
                  onToggleConversations={handleViewConversations}
                  hasActionPending={hasActionPending}
                  actionableNotification={hasActionPending ? notification : null}
                  allActionNotifications={allActionableNotifications[client.id] || []}
                  isConversationsExpanded={isConversationsExpanded}
                  notificationCount={allActionableNotifications[client.id]?.length || 0}
                  isCampaignComplete={Boolean(clientCampaignCompletion[client.id]?.isCampaignComplete)}
                  resultsCell={showResultsColumn ? (
                    <Suspense fallback={<td className="clients-table__results-cell" aria-hidden="true" />}>
                      <ClientResultsCell
                        client={client}
                        isCampaignComplete={Boolean(clientCampaignCompletion[client.id]?.isCampaignComplete)}
                        onOpenResults={handleOpenResults}
                      />
                    </Suspense>
                  ) : null}
                  onStop={requestCampaignStop}
                  isStopping={stoppingClientId === client.id}
                  stopError={stopErrors[client.id] || ''}
                  onMarkComplete={requestMarkComplete}
                  onReopen={requestReopen}
                  isChangingCompletion={changingCompletionId === client.id}
                  completionError={completionErrors[client.id] || ''}
                  blockedOutbox={clientBlockedOutbox[client.id] || null}
                />
                {/* Expanded Conversations Row */}
                {isConversationsExpanded && (
                  <tr
                    className="expanded-row conversations-expanded-row"
                    ref={el => conversationRowRefs.current[client.id] = el}
                  >
                    <td colSpan={showResultsColumn ? 5 : 4} className="expanded-content">
                      <ConversationsPanel
                        client={client}
                        externalActionNotification={
                          externalActionTarget?.clientId === client.id
                            ? externalActionTarget.notification
                            : null
                        }
                        onExternalActionComplete={onExternalActionComplete}
                        autoScrollToNotification={
                          autoScrollToAction?.clientId === client.id
                            ? autoScrollToAction.notification
                            : null
                        }
                        onAutoScrollHandled={() => setAutoScrollToAction(null)}
                        onLoaded={() => {
                          // Wait for DOM to fully render before scrolling
                          setTimeout(() => {
                            const rowEl = conversationRowRefs.current[client.id];
                            if (rowEl) {
                              const rect = rowEl.getBoundingClientRect();
                              const viewportHeight = window.innerHeight;

                              // If the panel fits in viewport, center it
                              if (rect.height <= viewportHeight - 80) {
                                rowEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              } else {
                                // Panel is taller than viewport - scroll so top is near top of viewport
                                // with some padding for the client row header above
                                const scrollTarget = window.scrollY + rect.top - 100;
                                window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
                              }
                            }
                          }, 100);
                        }}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {startingClient && (
        <StartProjectModal
          key={startingClient.id}
          client={startingClient}
          mailboxConnected={mailboxConnected}
          onClose={closeStartModal}
          onConfirm={async (payload) => {
            const confirmationRequestId = startProjectRequestRef.current;
            const confirmationClient = startingClient;
            const confirmationIsCurrent = () => (
              confirmationRequestId === startProjectRequestRef.current
              && String(confirmationClient?.id || '') === String(payload?.clientId || '')
            );
            if (!confirmationIsCurrent()) return { accepted: false };

            const access = await verifyCampaignCreationAccess(refreshCampaignAccess);
            if (!confirmationIsCurrent()) return { accepted: false };
            if (!access.allowed) {
              closeStartModal();
              setMaintenanceDialogMessage(access.message || maintenanceMessage);
              return { accepted: false };
            }

            let launchClient;
            try {
              launchClient = await hydrateCampaignLaunchClient(confirmationClient);
            } catch (error) {
              if (!confirmationIsCurrent()) return { accepted: false };
              closeStartModal();
              setMaintenanceDialogMessage(campaignLaunchGuardMessage(error));
              return { accepted: false };
            }
            if (!confirmationIsCurrent()) return { accepted: false };

            if (campaignScriptContract(confirmationClient) !== campaignScriptContract(launchClient)) {
              closeStartModal();
              setMaintenanceDialogMessage(CAMPAIGN_CONTRACT_ERROR_MESSAGE);
              return { accepted: false };
            }

            const { assignedEmails, script, secondaryScript, emailScripts, personalizedEmails, followUpConfig, sendMode } = payload || {};
            const clientId = launchClient.id;
            const launchAskFields = Array.isArray(launchClient.askFields)
              ? launchClient.askFields
              : deriveAskFields(launchClient.columnConfig);
            // Both halves of the column contract travel with every item, and one
            // id ties the items of this launch together so the server can refuse
            // the launch as a whole rather than item by item. A fresh id per
            // attempt: a refused launch must never block a later relaunch.
            const launchNonRequestableFields = Array.isArray(launchClient.nonRequestableFields)
              ? launchClient.nonRequestableFields
              : deriveNonRequestableFields(launchClient.columnConfig);
            const launchId = `${clientId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            const launchValidationOptions = {
              askFields: launchAskFields,
              nonRequestableFields: launchNonRequestableFields,
            };
            const launchDrafts = personalizedEmails?.length > 0
              ? personalizedEmails.map(emailData => emailData.script)
              : [script];
            const launchIsSafe = launchDrafts.every(draft => (
              validateCampaignLaunchDraft(draft, launchValidationOptions).ok
            ));

            if (!launchIsSafe) {
              if (!confirmationIsCurrent()) return { accepted: false };
              setStartingClient(launchClient);
              return { accepted: false };
            }

            if (!confirmationIsCurrent()) return { accepted: false };
            if (!launchPayloadMatchesFreshCampaign(launchClient, payload)) {
              closeStartModal();
              setMaintenanceDialogMessage(CAMPAIGN_CONTRACT_ERROR_MESSAGE);
              return { accepted: false };
            }

            const expectedSourceContract = campaignLaunchSourceContract(launchClient);
            const validateCurrent = currentData => currentCampaignLaunchIsValid({
              baseClient: launchClient,
              currentData,
              expectedSourceContract,
              payload,
            });

            try {
              const uid = auth.currentUser?.uid;
              if (!uid) throw new Error('Not signed in');
              const updateData = { status: 'live' };
              if (followUpConfig) {
                updateData.followUpConfig = followUpConfig;
                updateData.followUpCount = followUpConfig.followUps?.length || 0;
              }
              if (sendMode) {
                updateData.sendMode = sendMode;
              }
              const clientUpdate = { clientId, data: updateData, validateCurrent };

              // Create individual outbox entries for each personalized email
              if (personalizedEmails && personalizedEmails.length > 0) {
                // Send individual personalized emails
                const launchBatch = prepareActionAuditOutboxBatch({
                  uid,
                  clientUpdate,
                  entries: personalizedEmails.map(emailData => {
                    // Build subject from property data if available
                    const property = emailData.property || {};
                    const subject = buildCampaignPropertySubject(property);

                    return {
                      audit: {
                        actionType: 'campaign_launch',
                        client: launchClient,
                        subject: subject || '',
                        finalBody: emailData.script,
                        finalRecipients: [emailData.email],
                        source: 'dashboard_campaign_launch',
                        reason: 'campaign_launch'
                      },
                      outbox: buildExactCampaignLaunchOutbox({
                        source: 'dashboard_campaign_launch',
                        actionType: 'campaign_launch',
                        assignedEmails: [emailData.email], // Single email per entry
                        script: emailData.script, // The personalized script with [NAME] replaced
                        askFields: launchAskFields,
                        nonRequestableFields: launchNonRequestableFields,
                        launchId,
                        clientId,
                        contactName: emailData.contactName,
                        firstName: emailData.firstName, // First name used in greeting
                        isPersonalized: true,
                        // Property-specific data for accurate tracking
                        rowNumber: property.rowIndex ?? null,
                        subject: subject, // Pre-computed subject from property
                        property: property.address ? {
                          address: property.address,
                          city: property.city || '',
                          propertyName: property.propertyName || ''
                        } : null,
                        // Combined send mode (only present when this broker's rows collapse into one email)
                        ...(emailData.sendMode ? { sendMode: emailData.sendMode } : {}),
                        ...(emailData.combinedSubject ? { combinedSubject: emailData.combinedSubject } : {}),
                        createdAt: serverTimestamp() // For proper send ordering
                      })
                    };
                  })
                });

                await launchBatch.commit();
              } else {
                // Fallback to original behavior for backward compatibility
                const launchBatch = prepareActionAuditOutboxBatch({
                  uid,
                  clientUpdate,
                  entries: [{
                    audit: {
                      actionType: 'campaign_launch',
                      client: launchClient,
                      subject: '',
                      finalBody: script,
                      finalRecipients: assignedEmails || [],
                      source: 'dashboard_campaign_launch',
                      reason: 'campaign_launch'
                    },
                    outbox: buildExactCampaignLaunchOutbox({
                      source: 'dashboard_campaign_launch',
                      actionType: 'campaign_launch',
                      assignedEmails,
                      script,
                      askFields: launchAskFields,
                      nonRequestableFields: launchNonRequestableFields,
                      launchId,
                      secondaryScript: secondaryScript || null,
                      emailScripts: emailScripts || [script],
                      clientId,
                      isPersonalized: false,
                      createdAt: serverTimestamp() // For proper send ordering
                    })
                  }]
                });
                await launchBatch.commit();
              }

              updateClientInCache(clientId, updateData);
            } catch (e) {
              console.error('❌ Failed to create outbox item:', e);
              if (e?.code === 'campaign-launch-precondition-failed') {
                setMaintenanceDialogMessage(CAMPAIGN_CONTRACT_ERROR_MESSAGE);
              } else {
                alert('Could not queue emails. Please try again.');
              }
            } finally {
              closeStartModal();
            }
          }}
        />
      )}

      {resultsClient && canUseResultsFeatures && CompletedResultsWorkspace && (
        <Suspense fallback={<div className="results-workspace-loading" style={{ padding: 24 }}>Loading results...</div>}>
          <CompletedResultsWorkspace
            client={resultsClient}
            onClose={() => setResultsClient(null)}
          />
        </Suspense>
      )}

      <CampaignMaintenanceDialog
        isOpen={Boolean(maintenanceDialogMessage)}
        message={maintenanceDialogMessage || maintenanceMessage}
        onClose={() => setMaintenanceDialogMessage(null)}
      />

      <ConfirmDialog
        open={Boolean(completionConfirm) && completionConfirm.mode === 'complete'}
        title={`Mark ${completionConfirm?.client?.name || 'campaign'} complete?`}
        description="SiteSift will stop watching this campaign for broker replies. Nothing is deleted and the campaign stays on your dashboard \u2014 you can reopen it at any time."
        confirmLabel="Mark complete"
        cancelLabel="Keep watching"
        isLoading={Boolean(changingCompletionId)}
        onConfirm={confirmCompletionChange}
        onCancel={() => setCompletionConfirm(null)}
      />

      <ConfirmDialog
        open={Boolean(completionConfirm) && completionConfirm.mode === 'reopen'}
        title={`Reopen ${completionConfirm?.client?.name || 'campaign'}?`}
        description="SiteSift will start watching this campaign for broker replies again, and will answer them as it did before."
        confirmLabel="Reopen campaign"
        cancelLabel="Leave it complete"
        isLoading={Boolean(changingCompletionId)}
        onConfirm={confirmCompletionChange}
        onCancel={() => setCompletionConfirm(null)}
      />

      <ConfirmDialog
        open={Boolean(stopConfirmationClient)}
        title={`Stop ${stopConfirmationClient?.name || 'campaign'}?`}
        description="This stops all campaign automation and cancels unsent work. It preserves campaign history and does not delete the campaign."
        confirmLabel="Stop campaign"
        cancelLabel="Keep running"
        isLoading={Boolean(stoppingClientId)}
        onConfirm={confirmCampaignStop}
        onCancel={() => setStopConfirmationClient(null)}
      />

    </>
  );
}
